<template>
	<span class="csp-wrap" v-click-outside="closePicker">

		<!-- Trigger: small colored circle, click opens dropdown -->
		<button
			type="button"
			class="csp-trigger"
			:style="triggerStyle"
			:title="value ? value : 'Choose a color'"
			@click="togglePicker"
		>
			<!-- No inner content needed — the circle color speaks for itself -->
		</button>

		<!-- Dropdown panel -->
		<span v-if="showPicker" class="csp-dropdown">

			<span class="csp-swatch-row">
				<span
					v-for="swatch in palette"
					:key="swatch.hex"
					class="csp-swatch"
					:style="{ backgroundColor: swatch.hex }"
					:title="swatch.label"
					@click="selectColor(swatch.hex)"
				>
					<span v-if="swatch.hex === value" class="csp-check">✓</span>
				</span>

				<!-- Reset -->
				<span class="csp-swatch csp-reset" title="Remove color" @click="clearColor">✕</span>
			</span>

			<span class="csp-hex-row">
				<input
					type="text"
					class="csp-hex-input"
					:class="{ 'csp-hex-invalid': hexInvalid }"
					:value="hexValue"
					placeholder="#rrggbb"
					maxlength="7"
					@input="onHexInput($event.target.value)"
				/>
			</span>

		</span>
	</span>
</template>

<script>
// 20260416 -- Copilot (Claude Sonnet 4.6) -- Compact color picker: single circle trigger opens a floating dropdown panel

const PALETTE = [
	{ label: 'Slate',   hex: '#64748b' },
	{ label: 'Red',     hex: '#ef4444' },
	{ label: 'Orange',  hex: '#f97316' },
	{ label: 'Amber',   hex: '#f59e0b' },
	{ label: 'Lime',    hex: '#84cc16' },
	{ label: 'Green',   hex: '#10b981' },
	{ label: 'Teal',    hex: '#14b8a6' },
	{ label: 'Cyan',    hex: '#06b6d4' },
	{ label: 'Sky',     hex: '#0ea5e9' },
	{ label: 'Blue',    hex: '#3b82f6' },
	{ label: 'Indigo',  hex: '#6366f1' },
	{ label: 'Violet',  hex: '#8b5cf6' },
	{ label: 'Purple',  hex: '#a855f7' },
	{ label: 'Fuchsia', hex: '#d946ef' },
	{ label: 'Rose',    hex: '#f43f5e' },
	{ label: 'Brand',   hex: '#003c60' },
]

const HEX_REGEX = /^#[0-9a-f]{6}$/i

export default {
	name: 'ColorSwatchPicker',

	directives: {
		// 20260416 -- Copilot (Claude Sonnet 4.6) -- Click-outside directive to close dropdown when user clicks elsewhere
		clickOutside: {
			beforeMount(el, binding) {
				el._clickOutsideHandler = (event) => {
					if (!el.contains(event.target)) {
						binding.value()
					}
				}
				document.addEventListener('click', el._clickOutsideHandler, true)
			},
			unmounted(el) {
				document.removeEventListener('click', el._clickOutsideHandler, true)
			},
		},
	},

	props: {
		value: { type: String, default: null },
	},

	data() {
		return {
			palette: PALETTE,
			showPicker: false,
			hexValue: this.value || '',
			hexInvalid: false,
		}
	},

	computed: {
		triggerStyle() {
			return this.value
				? { backgroundColor: this.value }
				: { backgroundColor: '#e5e7eb' }
		},
	},

	watch: {
		value(newVal) {
			this.hexValue = newVal || ''
			this.hexInvalid = false
		},
	},

	methods: {
		togglePicker() {
			this.showPicker = !this.showPicker
		},

		closePicker() {
			this.showPicker = false
		},

		selectColor(hex) {
			this.$emit('input', hex)
			this.hexValue = hex
			this.hexInvalid = false
			this.showPicker = false
		},

		clearColor() {
			this.$emit('input', null)
			this.hexValue = ''
			this.hexInvalid = false
			this.showPicker = false
		},

		// 20260416 -- Copilot (Claude Sonnet 4.6) -- Validate hex input on each keystroke; emit only when valid
		onHexInput(raw) {
			this.hexValue = raw
			if (HEX_REGEX.test(raw)) {
				this.hexInvalid = false
				this.$emit('input', raw.toLowerCase())
				this.showPicker = false
			} else {
				this.hexInvalid = raw.length > 0
			}
		},
	},
}
</script>

<style scoped>
/* 20260416 -- Copilot (Claude Sonnet 4.6) -- Compact trigger + floating dropdown styles */

.csp-wrap {
	position: relative;
	display: inline-block;
}

/* The circle trigger button */
.csp-trigger {
	width: 22px;
	height: 22px;
	border-radius: 50%;
	border: 2px solid rgba(0, 0, 0, 0.15);
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	transition: border-color 0.15s ease, transform 0.1s ease;
	vertical-align: middle;
}

.csp-trigger:hover {
	border-color: rgba(0, 0, 0, 0.4);
	transform: scale(1.1);
}

/* Floating dropdown panel */
.csp-dropdown {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	z-index: 1050;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	padding: 8px;
	display: inline-flex;
	flex-direction: column;
	gap: 6px;
	min-width: 160px;
}

.csp-swatch-row {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
}

.csp-swatch {
	width: 20px;
	height: 20px;
	border-radius: 50%;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	border: 1px solid transparent;
	transition: transform 0.1s ease;
}

.csp-swatch:hover {
	transform: scale(1.2);
	border-color: rgba(0, 0, 0, 0.25);
}

.csp-check {
	color: #fff;
	font-size: 10px;
	font-weight: bold;
	text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
	line-height: 1;
}

.csp-reset {
	background: transparent;
	border: 1px solid #ccc;
	color: #888;
	font-size: 10px;
}

.csp-reset:hover {
	background: #f5f5f5;
	border-color: #999;
}

.csp-hex-row {
	display: flex;
}

.csp-hex-input {
	width: 100%;
	font-size: 11px;
	padding: 3px 6px;
	border: 1px solid #ccc;
	border-radius: 3px;
	font-family: monospace;
	outline: none;
}

.csp-hex-input:focus {
	border-color: #86b7fe;
}

.csp-hex-invalid {
	border-color: #dc3545 !important;
}
</style>
